import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  reportLostItem, getEligibleBookingsForLostItemReport,
  LOCATION_IN_VEHICLE_OPTIONS,
} from '../../services/disputeService';
import PhotoUploader from '../../components/PhotoUploader';
import { ACCEPTED_IMAGE_TYPES } from '../../services/storageService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function ReportLostItem() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  // Optional pre-fill from a trip detail link
  const prefilledBookingId = location.state?.bookingId || null;

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bookingId: prefilledBookingId || '',
    description: '',
    locationInVehicle: '',
    photos: [],  // [{ url, path }]
  });

  useEffect(() => {
    if (!userProfile?.id) return;
    getEligibleBookingsForLostItemReport(userProfile.id)
      .then(setBookings)
      .catch((err) => toast.error(err.message || 'Could not load trips'))
      .finally(() => setBookingsLoading(false));
  }, [userProfile?.id]);

  const handleSubmit = async () => {
    if (!form.bookingId) { toast.error('Select a trip'); return; }
    if (!form.description || form.description.trim().length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    if (!form.locationInVehicle) { toast.error('Select where in the vehicle'); return; }

    setSubmitting(true);
    try {
      const result = await reportLostItem({
        bookingId: form.bookingId,
        description: form.description.trim(),
        locationInVehicle: form.locationInVehicle,
        stagedPhotoUrls: form.photos.map(p => p.url),
      });
      toast.success(`Reported ${result.referenceNumber}. Your driver will respond within 48 hours.`);
      navigate(`/rider/lost-items/${result.caseId}`);
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  const selectedBooking = bookings.find(b => b.id === form.bookingId);

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Report a lost item</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Reports must be filed within 14 days of trip completion. Your driver will be notified
        and has 48 hours to respond.
      </p>

      {/* Trip selector */}
      <div className="form-group">
        <label className="form-label">Which trip? *</label>
        {bookingsLoading ? (
          <div className="p-3 text-muted text-[0.9rem]">Loading your recent trips...</div>
        ) : bookings.length === 0 ? (
          <div className="p-3.5 rounded-[10px] text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
            You have no eligible trips. Lost item reports can only be filed for completed trips within the last 14 days.
          </div>
        ) : (
          <select className="form-input form-select" value={form.bookingId}
            onChange={(e) => setForm({ ...form, bookingId: e.target.value })}>
            <option value="">Select a trip...</option>
            {bookings.map(b => {
              const completedAt = b.completedAt?.toDate?.() || new Date(b.completedAt);
              return (
                <option key={b.id} value={b.id}>
                  {b.referenceNumber || b.id.substring(0, 8)} — {completedAt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} — {b.destinationAddress?.substring(0, 40) || ''}
                </option>
              );
            })}
          </select>
        )}
        {selectedBooking && (
          <div className="p-2.5 rounded-lg bg-slate-50 mt-1.5 text-[0.82rem] text-body">
            Driver: <strong>{selectedBooking.driverName || 'Driver'}</strong> ·
            Vehicle: {selectedBooking.driverVehicleColour} {selectedBooking.driverVehicleMake} {selectedBooking.driverVehicleModel}
            {selectedBooking.driverPlate && <> · <span className="font-mono">{selectedBooking.driverPlate}</span></>}
          </div>
        )}
      </div>

      {/* Description */}
      <div className="form-group">
        <label className="form-label">Describe the item * (min 10 chars)</label>
        <textarea className="form-input" rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="e.g. Black iPhone 14 Pro with a clear case and blue ring holder on the back"
        />
        <div className="text-[0.75rem] text-muted mt-1">
          {form.description.length} characters
        </div>
      </div>

      {/* Location in vehicle */}
      <div className="form-group">
        <label className="form-label">Where in the vehicle? *</label>
        <select className="form-input form-select" value={form.locationInVehicle}
          onChange={(e) => setForm({ ...form, locationInVehicle: e.target.value })}>
          <option value="">Select a location...</option>
          {LOCATION_IN_VEHICLE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Photos */}
      <PhotoUploader
        userId={userProfile?.id}
        value={form.photos}
        onChange={(photos) => setForm({ ...form, photos })}
        maxFiles={5}
        acceptedTypes={ACCEPTED_IMAGE_TYPES}
        label="Photos of the item (optional)"
        helperText="Adding photos helps your driver identify the item. Up to 5 images."
      />

      {/* What happens next */}
      <div className="p-3.5 rounded-xl mb-4 text-[0.85rem] leading-normal" style={{ background: '#f0fdfa', border: '1px solid #99f6e4', color: '#0f766e' }}>
        <strong>What happens next:</strong>
        <ul className="mt-1.5 mb-0 pl-5">
          <li>Your driver gets notified and has 48 hours to respond.</li>
          <li>If the driver has your item and offers to return it, you'll be asked to offer a return fee (minimum $30).</li>
          <li>If the driver wants you to collect it, you can coordinate pickup via in-app messages and book a delivery trip.</li>
          <li>The driver is responsible for the item for 14 days. After that, they may dispose of it.</li>
        </ul>
      </div>

      <button onClick={handleSubmit} disabled={submitting || bookings.length === 0} className="w-full p-3.5 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[1rem]"
        style={{ opacity: (submitting || bookings.length === 0) ? 0.6 : 1 }}>
        {submitting ? 'Submitting...' : 'Submit report'}
      </button>
    <BackButtonBottom label="Back" />
    </div>
  );
}